import React from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { AppProvider } from './context/AppContext'
import { SubscriptionProvider } from './context/SubscriptionContext'
import { TourProvider } from './context/TourContext'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import OnboardingPage from './pages/OnboardingPage'
import LandingPage from './pages/LandingPage'
import AdminPanel from './pages/AdminPanel'
import AcceptInvitePage from './pages/AcceptInvitePage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import AppShell from './components/layout/AppShell'
import { AboutPage, ContactPage, PrivacyPage, TermsPage, SLAPage, AUPPage } from './pages/StaticPage'
import FAQPage from './pages/FAQPage'
import { EnvironmentBanner } from './components/ui/EnvironmentBanner'

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 16, color: '#605e5c', fontFamily: 'inherit' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #edebe9', borderTopColor: '#0078d4', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ margin: 0, fontSize: 14 }}>Loading...</p>
    </div>
  )
}

function ProtectedWorkspace() {
  const { user, organization, orgMember, loading } = useAuth()

  if (loading) return <LoadingScreen />
  if (!user) return <Navigate to="/login" replace />
  if (!organization) return <Navigate to="/onboarding" replace />

  return (
    <AppProvider
      orgId={organization.id}
      userId={user.id}
      orgMemberId={orgMember?.id ?? ''}
      orgMemberUserType={orgMember?.user_type ?? 'viewer'}
    >
      <SubscriptionProvider>
        <TourProvider>
          <Outlet />
        </TourProvider>
      </SubscriptionProvider>
    </AppProvider>
  )
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { user, organization, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (user && organization) return <Navigate to="/app" replace />
  if (user && !organization) return <Navigate to="/onboarding" replace />
  return <>{children}</>
}

function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, organization, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!user) return <Navigate to="/login" replace />
  if (organization) return <Navigate to="/app" replace />
  return <>{children}</>
}

function LandingRoute() {
  const { user, organization, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (user && organization) return <Navigate to="/app" replace />
  if (user && !organization) return <Navigate to="/onboarding" replace />
  return <LandingPage />
}

export default function App() {
  return (
    <AuthProvider>
      <EnvironmentBanner />
      <Routes>
        <Route path="/" element={<LandingRoute />} />
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/signup" element={<GuestRoute><SignupPage /></GuestRoute>} />
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/sla" element={<SLAPage />} />
        <Route path="/aup" element={<AUPPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route element={<ProtectedWorkspace />}>
          <Route path="/admin/*" element={<AdminPanel />} />
          <Route path="/app/*" element={<AppShell />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  )
}
